/** Chilean-style calendar day strings for Mi contenido / profile APIs (YYYY-MM-DD). */
export const YMD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export function formatYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function dateRangeForPreset(days: number): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));
  return { startDate: formatYmd(start), endDate: formatYmd(end) };
}

export function parseYmdRangeFromSearchParams(searchParams: URLSearchParams): {
  startDate: string;
  endDate: string;
} | null {
  const s = searchParams.get("startDate");
  const e = searchParams.get("endDate");
  if (s && e && YMD_REGEX.test(s) && YMD_REGEX.test(e)) {
    return { startDate: s, endDate: e };
  }
  return null;
}

export function isValidYmdRange(start: string, end: string): boolean {
  if (!YMD_REGEX.test(start) || !YMD_REGEX.test(end)) return false;
  return start <= end;
}

/** Clamp [start, end] to [minBound, maxBound] (YYYY-MM-DD lexicographic). */
export function clampYmdRangeToBounds(
  start: string,
  end: string,
  minBound: string,
  maxBound: string,
): { startDate: string; endDate: string } {
  let s = start < minBound ? minBound : start;
  let e = end > maxBound ? maxBound : end;
  if (s > maxBound) s = maxBound;
  if (e < minBound) e = minBound;
  if (s > e) {
    return { startDate: minBound, endDate: maxBound };
  }
  return { startDate: s, endDate: e };
}

/** Local calendar date from YYYY-MM-DD (avoids UTC shift). */
export function parseYmdToLocalDate(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function addDaysYmd(ymd: string, days: number): string {
  const d = parseYmdToLocalDate(ymd);
  d.setDate(d.getDate() + days);
  return formatYmd(d);
}

/** Prior range of equal length ending the day before `startDate`. */
export function computePreviousPeriodRange(
  startDate: string,
  endDate: string,
): { startDate: string; endDate: string } {
  const start = parseYmdToLocalDate(startDate);
  const end = parseYmdToLocalDate(endDate);
  const lengthDays =
    Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1;
  const previousEnd = addDaysYmd(startDate, -1);
  const previousStart = addDaysYmd(previousEnd, -(lengthDays - 1));
  return { startDate: previousStart, endDate: previousEnd };
}

/** True when [start, end] covers exactly one full calendar month. */
export function isFullCalendarMonth(startYmd: string, endYmd: string): boolean {
  const [sy, sm, sd] = startYmd.split("-").map(Number);
  const [ey, em, ed] = endYmd.split("-").map(Number);
  if (!sy || !sm || !sd || !ey || !em || !ed) return false;
  if (sy !== ey || sm !== em || sd !== 1) return false;
  return ed === new Date(ey, em, 0).getDate();
}

const CHILEAN_TZ = "America/Santiago";

/** Calendar YYYY-MM-DD in Chile for an ISO timestamp. */
export function chileanYmdFromIso(iso: string): string {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: CHILEAN_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const y = parts.find((p) => p.type === "year")?.value ?? "0000";
  const m = parts.find((p) => p.type === "month")?.value ?? "01";
  const day = parts.find((p) => p.type === "day")?.value ?? "01";
  return `${y}-${m}-${day}`;
}

export function defaultGoLinkGranularityForDays(days: number): "day" | "hour" | "minute" {
  if (days <= 2) return "hour";
  if (days <= 14) return "hour";
  return "day";
}

export type MiContenidoDateFilterCompany = {
  miContenidoDateFilterMinStartDate?: string | null;
  miContenidoDateFilterMaxEndDate?: string | null;
} | null;

export type MiContenidoDateFilterUser = {
  miContenidoDateFilterMinStartDate?: string | null;
  miContenidoDateFilterMaxEndDate?: string | null;
} | null;

/** Configured policy window when both effective Mi contenido dates are set (user override || company). */
export function getMiContenidoPolicyDateRange(
  company: MiContenidoDateFilterCompany,
  user?: MiContenidoDateFilterUser,
): { startDate: string; endDate: string } | null {
  const min =
    (typeof user?.miContenidoDateFilterMinStartDate === "string" &&
    user.miContenidoDateFilterMinStartDate.trim()
      ? user.miContenidoDateFilterMinStartDate.trim()
      : null) ||
    (typeof company?.miContenidoDateFilterMinStartDate === "string" &&
    company.miContenidoDateFilterMinStartDate.trim()
      ? company.miContenidoDateFilterMinStartDate.trim()
      : null);
  const max =
    (typeof user?.miContenidoDateFilterMaxEndDate === "string" &&
    user.miContenidoDateFilterMaxEndDate.trim()
      ? user.miContenidoDateFilterMaxEndDate.trim()
      : null) ||
    (typeof company?.miContenidoDateFilterMaxEndDate === "string" &&
    company.miContenidoDateFilterMaxEndDate.trim()
      ? company.miContenidoDateFilterMaxEndDate.trim()
      : null);
  if (min && max && isValidYmdRange(min, max)) {
    return { startDate: min, endDate: max };
  }
  return null;
}

/**
 * Formerly froze non–platform-admin users to the full company policy window.
 * Policy min/max are picker bounds only (like clips): users can move within them.
 * Kept for call-site compatibility; always unlocked.
 */
export function isMiContenidoDateRangeLocked(
  _userRole: string | undefined,
  _company: MiContenidoDateFilterCompany,
  _user?: MiContenidoDateFilterUser,
): boolean {
  return false;
}

export function buildMiContenidoDateQueryString(
  startDate: string,
  endDate: string,
  options?: { podcastId?: string; programId?: string; brandKey?: string },
): string {
  const p = new URLSearchParams();
  p.set("startDate", startDate);
  p.set("endDate", endDate);
  if (options?.podcastId) p.set("podcastId", options.podcastId);
  if (options?.programId) p.set("programId", options.programId);
  if (options?.brandKey) p.set("brandKey", options.brandKey);
  return p.toString();
}
